import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, Calendar, MapPin, Eye } from 'lucide-react';
import axios from 'axios';
import { toast } from 'react-toastify';
import config from '../config';
import AgeGateModal, { isAgeGatePassed } from '../components/AgeGateModal';
import SeoHead from '../components/SeoHead';
import MediaLightbox from '../components/MediaLightbox';
import DetailAdSlot from '../components/DetailAdSlot';
import HeaderAdBanner from '../components/HeaderAdBanner';
import { formatViews } from '../utils/formatViews';
import { carPath, carPublicUrl, isMongoObjectId, normalizePlateSlug } from '../utils/carUrl';

const CarDetails = () => {
  const params = useParams();
  // /car/:plate/:id  or legacy /car/:id
  const carId = params.plate && params.id
    ? params.id
    : (isMongoObjectId(params.id) ? params.id : params.plate);
  const plateParam = params.plate && params.id ? params.plate : null;

  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const [car, setCar] = useState(null);
  const [loading, setLoading] = useState(true);
  const [ageOk, setAgeOk] = useState(false);
  const [showAgeGate, setShowAgeGate] = useState(false);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const viewRegistered = useRef(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    viewRegistered.current = false;
    setLoading(true);
    fetchCar();
    try {
      localStorage.removeItem('ageConfirmed');
    } catch {
      /* ignore */
    }
    const ok = isAgeGatePassed();
    setAgeOk(ok);
    setShowAgeGate(!ok);
  }, [carId]);

  useEffect(() => {
    if (!ageOk || !carId || viewRegistered.current) return;

    const sessionKey = `car_viewed_${carId}`;
    try {
      if (sessionStorage.getItem(sessionKey) === '1') return;
    } catch {
      /* ignore */
    }

    viewRegistered.current = true;
    axios
      .post(`${config.API_URL}/api/cars/${carId}/view`)
      .then((res) => {
        try {
          sessionStorage.setItem(sessionKey, '1');
        } catch {
          /* ignore */
        }
        if (typeof res.data?.views === 'number') {
          setCar((prev) => (prev ? { ...prev, views: res.data.views } : prev));
        }
      })
      .catch(() => {
        viewRegistered.current = false;
      });
  }, [ageOk, carId]);

  // Canonical descriptive URL
  useEffect(() => {
    if (!car?._id || !car.licensePlate) return;
    const canonical = carPath(car);
    const current = `${window.location.pathname}`;
    if (current !== canonical) {
      navigate(canonical, { replace: true });
    }
  }, [car, navigate]);

  const fetchCar = async () => {
    if (!carId) {
      setLoading(false);
      navigate('/');
      return;
    }
    try {
      const response = await axios.get(`${config.API_URL}/api/cars/${carId}`);
      setCar(response.data);
    } catch (error) {
      console.error('Ошибка загрузки данных:', error);
      toast.error(t('common.error') || 'Error');
      navigate('/');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    const locale = (i18n.language || 'uz').startsWith('ru') ? 'ru-RU' : 'uz-UZ';
    return new Date(dateString).toLocaleDateString(locale, {
      year: 'numeric',
      day: 'numeric',
      month: 'long',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="container page-shell">
        <div className="loading">
          <div className="spinner" />
          <p>{t('common.loading')}</p>
        </div>
      </div>
    );
  }

  if (!car) {
    return (
      <div className="container page-shell">
        <div className="empty-state">
          <h3>{t('carDetails.notFound')}</h3>
          <Link to="/" className="btn btn-primary">
            {t('header.home')}
          </Link>
        </div>
      </div>
    );
  }

  const plateLabel = car.licensePlate || normalizePlateSlug(plateParam) || '';
  const mediaItems = [
    ...(car.photos || []).map((photo, i) => ({
      type: 'image',
      src: `${config.API_URL}/uploads/${photo}`,
      alt: `${plateLabel} — foto ${i + 1}`
    })),
    ...(car.videos || []).map((video, i) => ({
      type: 'video',
      src: `${config.API_URL}/uploads/${video}`,
      alt: `${plateLabel} — video ${i + 1}`
    }))
  ];

  const openLightbox = (idx) => {
    setLightboxIndex(idx);
    setLightboxOpen(true);
  };

  const descBits = [
    plateLabel ? `Davlat raqami ${plateLabel}` : null,
    car.description ? String(car.description).slice(0, 140) : null,
    'Avto Tarix — DTP va avtomobil tarixi'
  ].filter(Boolean);
  const ogImage = car.photos?.[0]
    ? `${config.API_URL}/uploads/${car.photos[0]}`
    : undefined;

  return (
    <div className="container page-shell">
      <SeoHead
        title={`${plateLabel} — avtomobil tarixi`}
        description={descBits.join('. ')}
        canonical={carPublicUrl(car)}
        image={ogImage}
        type="article"
      />

      <HeaderAdBanner />

      <nav aria-label="breadcrumb" style={{ marginBottom: 12, fontSize: 13, color: 'var(--mute)' }}>
        <Link to="/" style={{ color: 'inherit' }}>Avto Tarix</Link>
        {' / '}
        <Link to="/search" style={{ color: 'inherit' }}>{t('header.search') || 'Qidiruv'}</Link>
        {' / '}
        <span style={{ color: 'var(--ink)' }}>{plateLabel}</span>
      </nav>

      <button type="button" onClick={() => navigate(-1)} className="btn btn-secondary" style={{ marginBottom: 20 }}>
        <ArrowLeft size={16} />
        {t('carDetails.back')}
      </button>

      <div className="form-panel" style={{ maxWidth: 860 }}>
        <h1 className="car-entry__plate" style={{ marginBottom: 16, fontSize: 22 }}>
          {car.licensePlate}
        </h1>

        <div className="car-entry__meta" style={{ marginBottom: 20 }}>
          <span>
            <Calendar size={14} />
            {formatDate(car.createdAt)}
          </span>
          {car.location?.address && (
            <span>
              <MapPin size={14} />
              {car.location.address}
            </span>
          )}
          <span className="views-count" title={t('home.views')}>
            <Eye size={14} strokeWidth={2.25} />
            {formatViews(car.views)}
          </span>
        </div>

        {car.description && (
          <div style={{ marginBottom: 28 }}>
            <h2 className="form-label">{t('carDetails.description')}</h2>
            <p style={{ color: 'var(--slate)', lineHeight: 1.65, fontSize: 15 }}>{car.description}</p>
          </div>
        )}

        <DetailAdSlot />

        {!ageOk ? (
          <div className="notice notice--warn" style={{ marginBottom: 0 }}>
            <div>
              <div className="notice__title">{t('ageRestriction.title')}</div>
              <div className="notice__text">{t('ageRestriction.unlockMedia')}</div>
              <button
                type="button"
                className="btn btn-danger"
                style={{ marginTop: 12 }}
                onClick={() => setShowAgeGate(true)}
              >
                {t('ageRestriction.enter')}
              </button>
            </div>
          </div>
        ) : (
          <>
            {car.photos?.length > 0 && (
              <div style={{ marginBottom: 28 }}>
                <h2 className="form-label">
                  {t('carDetails.photos')} ({car.photos.length})
                </h2>
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
                    gap: 12
                  }}
                >
                  {car.photos.map((photo, index) => (
                    <button
                      key={index}
                      type="button"
                      className="media-thumb"
                      onClick={() => openLightbox(index)}
                    >
                      <img
                        src={`${config.API_URL}/uploads/${photo}`}
                        alt={`${plateLabel} — foto ${index + 1}`}
                      />
                    </button>
                  ))}
                </div>
              </div>
            )}

            {car.videos?.length > 0 && (
              <div style={{ marginBottom: 8 }}>
                <h2 className="form-label">
                  {t('carDetails.videos')} ({car.videos.length})
                </h2>
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
                    gap: 12
                  }}
                >
                  {car.videos.map((video, index) => {
                    const mediaIndex = (car.photos?.length || 0) + index;
                    return (
                      <button
                        key={index}
                        type="button"
                        className="media-thumb media-thumb--video"
                        onClick={() => openLightbox(mediaIndex)}
                      >
                        <video
                          src={`${config.API_URL}/uploads/${video}`}
                          muted
                          preload="metadata"
                          playsInline
                        />
                        <span className="media-thumb__play">▶</span>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {(!car.photos || car.photos.length === 0) && (!car.videos || car.videos.length === 0) && (
              <p style={{ color: 'var(--mute)' }}>{t('carDetails.noMedia')}</p>
            )}
          </>
        )}
      </div>

      <MediaLightbox
        open={lightboxOpen}
        items={mediaItems}
        index={lightboxIndex}
        onClose={() => setLightboxOpen(false)}
        onIndexChange={setLightboxIndex}
      />

      <AgeGateModal
        open={showAgeGate}
        onCancel={() => {
          setShowAgeGate(false);
          if (!ageOk) navigate(-1);
        }}
        onConfirm={() => {
          setAgeOk(true);
          setShowAgeGate(false);
        }}
      />
    </div>
  );
};

export default CarDetails;
